<template>
  <div>
    <v-container fluid>
      <!-- Page header -->
      <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">Voice Configs</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <v-card class="card2" style="margin-top: 1rem">
        <v-card-text>
          <v-row align="center">
            <v-col cols="12" md="5">
              <div class="page-title">Voice Configuration List</div>
            </v-col>
            <v-col cols="12" md="7" class="d-flex flex-wrap justify-end gap-2">
              <!-- Search by name -->
              <v-text-field
                id="field-search-voiceconfigs"
                v-model="searchName"
                placeholder="Search by name"
                variant="outlined"
                density="comfortable"
                class="custom-search-field mr-3"
                hide-details
                clearable
                @keydown.enter="Search"
                @click:clear="Search"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="#001763" size="25">mdi-magnify</v-icon>
                </template>
              </v-text-field>

              <!-- Add -->
              <v-btn v-if="canAdd" id="button-add-voiceconfigs" variant="elevated" class="custom-document-btn" @click="Add()">
                <v-icon size="23" class="mr-2">mdi-plus</v-icon>
                <span class="btn-text">Voice Config</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Table -->
        <div class="table-section-spacing">
          <v-data-table-server
            v-model:options="options"
            :items-per-page="itemsPerPage"
            :items-length="searchTotalTable"
            :items="dataTable"
            :headers="headers"
            :loading="loadingTable"
            class="elevation-1 my-data-table"
            density="comfortable"
            fixed-header
          >
            <template v-slot:item="{ item }">
              <tr>
                <td style="text-align: center">{{ item.number }}</td>
                <td style="text-align: center">
                  <span class="display-id-text">{{ item.displayId }}</span>
                </td>
                <td style="text-align: left">
                  <span class="name-text">{{ item.name }}</span>
                </td>
                <td style="text-align: left">
                  <div class="chip-group">
                    <v-chip size="small" variant="outlined" class="section-chip">
                      {{ item.sttCount }}/{{ item.ttsCount }}/{{ item.geminiCount }}
                      <v-tooltip activator="parent" location="top">STT / TTS / Gemini TTS</v-tooltip>
                    </v-chip>
                  </div>
                </td>
                <td style="text-align: left">
                  <div v-if="item.usedBy && item.usedBy.length > 0" class="chip-group">
                    <v-chip
                      v-for="mode in item.usedBy"
                      :key="'usedby-' + item.id + '-' + mode"
                      size="small"
                      :color="mode === 'Tax' ? 'deep-purple' : 'primary'"
                      class="usedby-chip"
                    >
                      {{ mode }}
                    </v-chip>
                  </div>
                  <span v-else class="usedby-empty">
                    &mdash;
                    <v-tooltip activator="parent" location="top">Not used by any mobile configuration</v-tooltip>
                  </span>
                </td>
                <td style="text-align: left">
                  <div class="date-cell">
                    <div>{{ formatDateTime(item.updateDate).date }}</div>
                    <div class="time-text">{{ item.updateBy }}</div>
                  </div>
                </td>
                <td class="sticky-action" style="text-align: center">
                  <div class="action-buttons-group">
                    <v-btn id="button-edit-voiceconfigs" variant="text" density="comfortable" icon class="action-btn edit-btn" @click="Edit(item)">
                      <v-icon size="20" color="primary">{{ canEdit ? 'mdi-pencil-outline' : 'mdi-eye-outline' }}</v-icon>
                      <v-tooltip activator="parent" location="top">{{ canEdit ? 'Edit' : 'View' }}</v-tooltip>
                    </v-btn>
                    <div
                      v-if="canDelete"
                      id="button-delete-voiceconfigs"
                      class="trash-icon-wrapper"
                      :class="{ 'is-disabled': btnDisableDelete(item) }"
                    >
                      <v-icon
                        @click="btnDisableDelete(item) ? null : Delete(item)"
                        :style="{ cursor: btnDisableDelete(item) ? 'not-allowed' : 'pointer' }"
                        :color="btnDisableDelete(item) ? 'grey-lighten-1' : 'error'"
                        size="20"
                        >mdi-trash-can-outline</v-icon
                      >
                      <v-tooltip v-if="btnDisableDelete(item)" activator="parent" location="top">
                        Used by {{ item.usedBy.join(' and ') }} mode &mdash; remove it there first.
                      </v-tooltip>
                      <v-tooltip v-else activator="parent" location="top">Delete</v-tooltip>
                    </div>
                  </div>
                </td>
              </tr>
            </template>

            <template v-slot:no-data>
              <div class="empty-state">
                <v-icon size="48" color="grey-lighten-1">mdi-account-voice</v-icon>
                <div class="empty-text">No voice configuration found</div>
              </div>
            </template>
          </v-data-table-server>
        </div>
      </v-card>
    </v-container>

    <!-- loading -->
    <v-overlay :model-value="dialog" class="align-center justify-center" persistent>
      <v-card class="loading-card" elevation="8">
        <v-card-text class="text-center">
          <v-progress-circular indeterminate size="64" color="primary" class="mb-4"></v-progress-circular>
          <h3 class="loading-title">Loading...</h3>
          <p class="loading-subtitle">Please wait</p>
        </v-card-text>
      </v-card>
    </v-overlay>
  </div>
</template>

<script>
import checkPermissionTwo from '@/api/function.js';

export default {
  name: 'VoiceConfigs',
  inject: ['flagNav', 'FontColorTitle', 'SubNavbarBackGround'],
  mixins: [checkPermissionTwo],
  components: {},
  data: () => ({
    today: null,
    searchName: null,
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
    },
    itemsPerPage: 10,
    dataTable: [],
    searchTotalTable: 0,
    totalTable: 0,
    loadingTable: false,
    dialog: false,
    isFirst: true,
    isSearch: false,
  }),
  computed: {
    canAdd() {
      return !!(this.$pagePermission && this.$pagePermission.canAdd('VoiceConfigs'));
    },
    canEdit() {
      return !!(this.$pagePermission && this.$pagePermission.canEdit('VoiceConfigs'));
    },
    canDelete() {
      return !!(this.$pagePermission && this.$pagePermission.canDelete('VoiceConfigs'));
    },
    headers() {
      return [
        {
          title: 'No.',
          align: 'center',
          sortable: false,
          width: '80px',
          minWidth: '60px',
        },
        {
          title: 'Voice Config ID',
          key: 'displayId',
          value: 'displayId',
          align: 'center',
          sortable: true,
          width: '140px',
          minWidth: '130px',
        },
        {
          title: 'Name',
          key: 'Name',
          value: 'Name',
          align: 'left',
          sortable: true,
          width: 'auto',
          minWidth: '200px',
        },
        {
          title: 'Sections',
          align: 'left',
          sortable: false,
          width: '160px',
          minWidth: '140px',
        },
        {
          title: 'Used by',
          align: 'left',
          sortable: false,
          width: '180px',
          minWidth: '160px',
        },
        {
          title: 'Updated',
          key: 'UpdateDate',
          value: 'UpdateDate',
          align: 'left',
          sortable: true,
          width: '180px',
          minWidth: '160px',
        },
        {
          title: 'Action',
          align: 'center',
          sortable: false,
          width: '120px',
          minWidth: '110px',
          class: 'sticky-action',
        },
      ];
    },
  },
  watch: {
    options: {
      handler() {
        this.Get();
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    Get() {
      let self = this;
      self.loadingTable = true;

      let { page, itemsPerPage, sortBy } = self.options;
      let sortField = null;
      let sortOrder = null;

      if (sortBy && sortBy.length > 0) {
        sortField = sortBy[0].key;
        sortOrder = sortBy[0].order === 'desc';
      }

      let temp = {
        page: self.isSearch ? 1 : page,
        perPage: itemsPerPage != -1 ? itemsPerPage : self.totalTable,
        name: self.searchName,
        sortBy: sortField,
        sortDesc: sortOrder,
      };

      self.$call.GetTableVoiceConfiguration(
        temp,
        (dataResponse) => {
          if (dataResponse.status == 0 && dataResponse.data) {
            self.dataTable = dataResponse.data.data || [];
            self.searchTotalTable = dataResponse.data.total || 0;
            self.totalTable = self.isFirst ? dataResponse.data.total || 0 : self.totalTable;
          } else {
            self.dataTable = [];
            self.searchTotalTable = 0;
            self.totalTable = 0;
          }
          self.loadingTable = false;
          self.isSearch = false;
          self.isFirst = false;
        },
        (errorResource) => {
          self.dataTable = [];
          self.searchTotalTable = 0;
          self.loadingTable = false;
          self.isSearch = false;
          console.error(errorResource);
        }
      );
    },
    Search() {
      let self = this;
      self.isSearch = true;
      if (self.options.page !== 1) {
        // the options watcher will fire Get()
        self.options.page = 1;
      } else {
        self.Get();
      }
    },
    Add() {
      this.$router.push({ name: 'VoiceConfigDetail' });
    },
    Edit(item) {
      this.$router.push({
        name: 'VoiceConfigDetail',
        query: {
          id: item.id,
        },
      });
    },
    // A config referenced by a mobile configuration cannot be deleted — the server refuses it too.
    btnDisableDelete(item) {
      return !!(item && item.usedBy && item.usedBy.length > 0);
    },
    Delete(item) {
      let self = this;
      try {
        self.$swal
          .fire({
            title: 'Delete Voice Config',
            text: 'Are you sure you want to delete this voice configuration? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: self.$t('confirm'),
            cancelButtonText: self.$t('cancel'),
            reverseButtons: false,
          })
          .then((result) => {
            if (result.isConfirmed) {
              self.dialog = true;
              self.$call.DeleteVoiceConfiguration(
                item.id,
                (dataResponse) => {
                  self.dialog = false;
                  if (dataResponse.status == 0) {
                    self.$swal.fire({
                      icon: 'success',
                      title: dataResponse.message || 'Deleted successfully',
                      showConfirmButton: false,
                      timer: 1500,
                    });
                    self.Get();
                  } else {
                    self.$swal.fire({
                      icon: 'error',
                      title: dataResponse.message || 'Failed to delete',
                      showConfirmButton: true,
                      confirmButtonColor: '#d33',
                    });
                  }
                },
                (errorResource) => {
                  self.dialog = false;
                  console.error(errorResource);
                  self.$swal.fire({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Failed to delete voice configuration',
                    confirmButtonColor: '#d33',
                  });
                }
              );
            }
          });
      } catch (error) {
        console.error(error);
      }
    },
    formatDateTime(dateTime) {
      if (!dateTime) return { date: null, time: null };

      const d = new Date(dateTime);
      if (isNaN(d.getTime())) return { date: dateTime, time: null };

      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const date = `${day}/${month}/${year}`;

      const hours = String(d.getHours()).padStart(2, '0');
      const minutes = String(d.getMinutes()).padStart(2, '0');
      const seconds = String(d.getSeconds()).padStart(2, '0');
      const time = `${hours}:${minutes}:${seconds}`;

      return { date, time };
    },
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1;
      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = '0' + dd;
      }
      if (mm < 10) {
        mm = '0' + mm;
      }
      this.today = dd + '/' + mm + '/' + yyyy;
    },
  },
  mounted() {
    this.GetDate();
  },
};
</script>

<style scoped>
.card2 {
  border-radius: 16px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.page-title {
  font-weight: 600;
  font-size: 24px;
  color: rgba(0, 0, 0, 0.87);
  text-align: left;
  display: flex;
  align-items: center;
  height: 100%;
  min-height: 48px;
}

/* Search field */
.custom-search-field {
  height: 48px !important;
  max-width: 320px;
}

.custom-search-field :deep(.v-field) {
  border-radius: 16px 16px !important;
  height: 48px !important;
  border-color: #c9c9c9 !important;
}

.custom-search-field :deep(.v-field__input) {
  display: flex !important;
  align-items: center !important;
  min-height: 48px !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.custom-search-field :deep(.v-field__prepend-inner) {
  display: flex !important;
  align-items: center !important;
  padding-left: 5px !important;
}

.custom-search-field :deep(.v-input__control) {
  height: 48px !important;
}

.custom-document-btn {
  color: #ffff;
  text-transform: none;
  box-shadow: 0 2px 4px rgba(80, 72, 151, 0.3);
  border-radius: 16px !important;
  height: 48px !important;
  min-width: 170px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: linear-gradient(90deg, #504897 0%, #4b2667 100%);
}

.custom-document-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(80, 72, 151, 0.4);
}

/* Table spacing from the card edge */
.table-section-spacing {
  padding: 0 16px 16px;
}

:deep(.v-data-table) {
  border: 1px solid #e1e1e1;
  border-radius: 24px;
  overflow: hidden;
}

:deep(.v-table) {
  border-radius: 24px;
  overflow: hidden;
}

:deep(.v-table__wrapper thead th) {
  background-color: #f5f5f5 !important;
  color: #333333;
  font-size: 14px;
  font-weight: 600;
  height: 48px;
}

:deep(.my-data-table .v-table__wrapper) {
  max-height: 500px !important;
  overflow: auto !important;
}

:deep(.my-data-table table) {
  min-width: 1000px;
}

/* Sticky action column */
.sticky-action {
  position: sticky !important;
  right: 0px !important;
  background-color: #fff !important;
  z-index: 10 !important;
}

:deep(.my-data-table thead th.sticky-action) {
  right: 0px !important;
  background-color: #f5f5f5 !important;
  z-index: 15 !important;
}

.action-buttons-group {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  min-width: 36px !important;
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

/* Disabled trash icon — visually convey it can't be clicked */
.trash-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
}

.trash-icon-wrapper.is-disabled {
  opacity: 0.5;
  pointer-events: auto;
}

.trash-icon-wrapper.is-disabled .v-icon {
  cursor: not-allowed !important;
}

.name-text {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.display-id-text {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

.chip-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.section-chip {
  font-size: 12px;
  font-weight: 500;
}

.usedby-chip {
  font-size: 12px;
  font-weight: 500;
}

.usedby-empty {
  color: rgba(0, 0, 0, 0.38);
}

.date-cell {
  text-align: left;
}

.time-text {
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.6);
  margin-top: 2px;
}

:deep(.v-data-table tbody tr) {
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.v-data-table tbody tr:hover) {
  background: rgba(0, 0, 0, 0.02);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 0;
}

.empty-text {
  color: rgba(0, 0, 0, 0.6);
  font-size: 14px;
}

.loading-card {
  border-radius: 16px;
  padding: 8px 16px;
}

.loading-title {
  font-size: 1.1rem;
  font-weight: 600;
}

.loading-subtitle {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}

@media (max-width: 600px) {
  .table-section-spacing {
    padding: 0 12px 12px;
  }

  .custom-search-field {
    max-width: 100%;
  }
}
</style>
